/**
 * useSync — React hook that triggers the Phase 4 sync engine on:
 *  1. Socket reconnect (lastOnlineTimestamp changes in ConnectionState).
 *  2. Visibility change (document becomes visible after being hidden).
 *  3. Network restore (ConnectionState.networkStatus offline → online) — forced
 *     past the backoff, since a real reconnection is exactly when missed events
 *     must be pulled.
 *
 * Deduplicates: skips if a sync is already in progress.
 * Wraps all sync calls with the cross-tab sync lock.
 * Exposes status: "idle" | "syncing" | "error".
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useConnectionState } from "@/zustand/ConnectionState";
import { runSync } from "@/indexedDB/services/syncEngine";
import { withSyncLock } from "@/indexedDB/services/syncLock";
import {
  beginChatListReconciliation,
  endChatListReconciliation,
} from "@/indexedDB/services/syncScheduler";
import { featureFlags } from "@/config/featureFlags";
import { createLogger } from "@/utils/debug";

const log = createLogger("Sync");

export type SyncStatus = "idle" | "syncing" | "error";

/** Minimum interval between sync attempts (ms) to prevent hammering. */
const MIN_SYNC_INTERVAL_MS = 30_000;

export function useSync() {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle");
  const isSyncingRef = useRef(false);
  const lastSyncAttemptRef = useRef(0);

  const triggerSync = useCallback(async (options?: { force?: boolean }) => {
    if (!featureFlags.useNewSync) {
      return;
    }
    if (isSyncingRef.current) {
      return;
    }

    // Backoff: skip if last attempt was too recent (Issue 5) — UNLESS forced.
    // A genuine network offline→online transition forces a sync: it's exactly
    // when missed events must be pulled, and the backoff would otherwise
    // silently DROP that reconnect sync if a sync happened to run in the 30s
    // before the connection dropped (the trigger isn't retried). The isSyncing
    // guard above still prevents overlapping runs, so forcing can't hammer.
    const now = Date.now();
    if (
      !options?.force &&
      now - lastSyncAttemptRef.current < MIN_SYNC_INTERVAL_MS
    ) {
      return;
    }

    // Remember the prior attempt time so a no-op SKIP (another tab/instance
    // holds the cross-tab lock) can be rolled back below — a skip is NOT a sync.
    const prevSyncAttemptAt = lastSyncAttemptRef.current;
    isSyncingRef.current = true;
    lastSyncAttemptRef.current = now;
    setSyncStatus("syncing");

    // Phase-1 cold-start fix (TRIGGER-LEVEL race): raise the chat-list
    // reconciliation gate SYNCHRONOUSLY here — BEFORE the `await withSyncLock`
    // hop — so it is up before this run reaches ANY await. The decryption-retry
    // orchestrator fires on the SAME socket-connect event and can reach its
    // `await awaitChatListReconciliationIdle()` quickly; if the gate were only
    // raised inside `runSync` (which runs AFTER `withSyncLock` →
    // `navigator.locks.request`, i.e. several awaits later), the orchestrator
    // could observe depth 0 and slip a legacy `getMessage` through, re-opening a
    // narrowed starvation window. Raising it at the trigger site (the single
    // chokepoint every runSync invocation passes through) closes that window:
    // the gate is provably up before the first async hop on every path,
    // INCLUDING the lock-skipped path (another tab holds the lock → `runSync`
    // never runs, but the orchestrator in THIS tab still defers correctly until
    // we release below). The chokepoint `begin` does NOT await the gate, so it
    // cannot deadlock the reconciliation. Balanced in `finally`.
    beginChatListReconciliation();
    try {
      const ran = await withSyncLock(() => runSync());
      if (!ran) {
        // P0-c: withSyncLock skipped (didn't queue) because another tab/instance
        // already holds the lock — this trigger ran nothing. Restore the backoff
        // anchor so this skip doesn't burn the 30s window: otherwise, if the
        // lock-holder fails before committing cursors, this tab would have
        // silently dropped its trigger AND suppressed non-forced follow-ups for
        // 30s, stalling reconciliation until an unrelated trigger.
        lastSyncAttemptRef.current = prevSyncAttemptAt;
      }
      setSyncStatus("idle");
    } catch (err) {
      // Log the cause — otherwise a field failure (network, contract mismatch,
      // Dexie error, IncompleteFullSyncError) only flips status to "error" with
      // no context to debug from.
      log.warn("[sync] runSync failed", err);
      setSyncStatus("error");
    } finally {
      // Release the gate raised before `withSyncLock`. Balanced on EVERY path
      // (success, lock-skip, throw) so the gate is never leaked from the trigger.
      endChatListReconciliation();
      isSyncingRef.current = false;
    }
  }, []);

  // -- Socket connect/reconnect trigger --
  // Fires on BOTH initial connect (login) and reconnects — this is intentional.
  // The sync engine handles first-time sync (no cursor → full chat list fetch)
  // and delta sync (has cursor → fetch changes only).
  const lastOnlineTimestamp = useConnectionState((s) => s.lastOnlineTimestamp);

  useEffect(() => {
    if (lastOnlineTimestamp) {
      triggerSync();
    }
  }, [lastOnlineTimestamp, triggerSync]);

  // -- Visibility change trigger --
  // Uses raw visibilitychange listener because the existing isAppVisible()
  // utility from src/utils/visibilityState.ts is a polling function (no
  // callback registration). We still need the event-driven approach here
  // to trigger sync on app resume. The iOS Safari edge cases are handled
  // by the visibilitychange event itself on modern iOS versions.
  // TODO: Phase 5 — consider extending visibilityState.ts with an
  // onChange callback API, then migrate this listener.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        triggerSync();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [triggerSync]);

  // -- Network restore trigger (offline → online) --
  // A real browser-network transition (navigator online/offline, surfaced via
  // ConnectionState.networkStatus by useNetworkStatus) FORCES a sync, bypassing
  // the 30s backoff. Note useNetworkStatus.handleOnline ALSO bumps
  // lastOnlineTimestamp on the same `online` event, so the socket-reconnect
  // trigger above fires too — but as the NON-forced path it gets dropped if a
  // sync ran in the prior 30s. This force path exists to cover exactly that
  // backoff window (and the tab may stay focused, so visibilitychange won't
  // help either); otherwise the client can stay unreconciled until the next
  // refocus/reconnect/refresh. The isSyncing guard dedups the two triggers
  // when both fire on the same restore.
  const networkStatus = useConnectionState((s) => s.networkStatus);
  const prevNetworkStatusRef = useRef(networkStatus);
  useEffect(() => {
    const prev = prevNetworkStatusRef.current;
    prevNetworkStatusRef.current = networkStatus;
    if (prev === "offline" && networkStatus === "online") {
      triggerSync({ force: true });
    }
  }, [networkStatus, triggerSync]);

  return { syncStatus };
}
